import { z } from 'zod'

import type { DisplayCurrency } from '@/lib/currency/map'
import { VALUE_SCHEMAS, type ValueKind, type ValueOf } from '@/lib/facts/values'
import type { Locale } from '@/i18n/routing'

import type { ValueFormatters } from './format'

/*
 * The variable grammar: a name in a message is, in order, an `editorial_*` constant, an
 * underscore path over the reader's fact schema that reaches a value, or a sub-template —
 * the store key it names, whose message renders where the name stands. The renderer
 * resolves a name against a page's values and messages; `content-lint` types it against
 * the declarations and the keys of the store files the reader reads, by the same order.
 *
 * A kind says what a value is; a presentation picks which of the renderer's locale-correct
 * rules for that kind applies, chosen per sentence in copy. Copy writes it as the ICU
 * style, `kind` or `kind-presentation` (`{d, number, duration-long}`), the kind restating
 * what the declaration already types — the ICU argument type is formatjs's requirement
 * for any style and is not part of this grammar.
 */

/** What a name resolves against: one entity's values, in one locale and currency. */
export interface ResolutionContext<F> {
    readonly facts: F
    /** The entity the page is for — the key editorial constants are looked up by. */
    readonly slug: string
    readonly locale: Locale
    readonly currency: DisplayCurrency
    readonly format: ValueFormatters
}

/** A figure the facts do not yet supply, keyed by entity slug with a page-type default;
 *  a constant with neither is an absent variable. */
export type EditorialConstant = {
    [K in ValueKind]: {
        readonly kind: K
        readonly default?: ValueOf<K>
        readonly byEntity: Readonly<Record<string, ValueOf<K>>>
    }
}[ValueKind]

/** The variables a reader declares beyond its schema: its editorial constants, each
 *  declared with its kind. */
export interface VariableRegistry {
    readonly editorial: Readonly<Record<`editorial_${string}`, EditorialConstant>>
}

/** Each kind's closed presentation menu, the first the default; a kind absent here has one
 *  rule and takes no presentation. `money`'s and `distance`'s defaults have no word. */
export const PRESENTATIONS = {
    duration: ['short', 'long'],
    money: ['whole', 'native'],
    distance: ['whole'],
    weekday: ['long', 'short'],
    month: ['long', 'short'],
    date: ['long', 'short'],
    instant: ['long'],
} as const satisfies Partial<Record<ValueKind, readonly string[]>>

export type Presentation<K extends ValueKind> = K extends keyof typeof PRESENTATIONS
    ? (typeof PRESENTATIONS)[K][number]
    : never

/** A kind an ICU argument type can carry: every kind but `list`, which an argument of
 *  `number`, `date` or `time` cannot hold, so only a page type's facts declare it. */
export type StyleKind = Exclude<ValueKind, 'list'>

/** Each kind a message can type an argument as, and the value a caller hands it: the
 *  type-level twin of `VALUE_SCHEMAS`, so a kind's caller type is its schema's. */
export type KindToType = { readonly [K in StyleKind]: ValueOf<K> }

export type ParsedStyle =
    | {
          readonly status: 'vocabulary'
          readonly kind: StyleKind
          readonly presentation: string | undefined
      }
    | { readonly status: 'outside'; readonly problem: string }

const isKind = (word: string): word is ValueKind => Object.hasOwn(VALUE_SCHEMAS, word)
const EVERY_PRESENTATION = new Set<string>(Object.values(PRESENTATIONS).flat())

/** An ICU argument's style read as the vocabulary: in it, as a kind and an optional
 *  presentation from that kind's menu; or outside it, with the reason a reader can act on. */
export function parseStyle(style: unknown): ParsedStyle {
    if (typeof style !== 'string') {
        return { status: 'outside', problem: '`::` skeletons format by ICU, not by a value kind' }
    }
    const [word, ...rest] = style.split('-')
    const presentation = rest.length === 0 ? undefined : rest.join('-')
    if (!isKind(word)) {
        return EVERY_PRESENTATION.has(style)
            ? {
                  status: 'outside',
                  problem: `a presentation is written with its kind (duration-long), never alone`,
              }
            : { status: 'outside', problem: `${word} is not a value kind` }
    }
    if (word === 'list') {
        return {
            status: 'outside',
            problem: 'list is declared by a page type’s facts; no ICU argument type carries it',
        }
    }
    const menu: readonly string[] = Object.hasOwn(PRESENTATIONS, word)
        ? PRESENTATIONS[word as keyof typeof PRESENTATIONS]
        : []
    if (presentation !== undefined && !menu.includes(presentation)) {
        return {
            status: 'outside',
            problem: `${word} has no ${presentation} presentation; it ${
                menu.length === 0 ? 'takes none' : `has ${menu.join(', ')}`
            }`,
        }
    }
    return { status: 'vocabulary', kind: word, presentation }
}

export type NameForm =
    | { readonly form: 'editorial'; readonly constant: EditorialConstant | undefined }
    | { readonly form: 'path'; readonly segments: readonly string[] }
    | { readonly form: 'sub-template'; readonly key: string }

/** A name's reading by the grammar's order; a name that walks the fact schema to no value
 *  is read as the key of a sub-template, which may not exist. */
export function nameForm(
    name: string,
    { variables, registry }: Pick<VariableDeclarations<unknown>, 'variables' | 'registry'>,
): NameForm {
    if (name.startsWith('editorial_')) {
        return { form: 'editorial', constant: registry.editorial[name as `editorial_${string}`] }
    }
    const segments = name.split('_')
    return pathType(variables, segments, false).status === 'typed'
        ? { form: 'path', segments }
        : { form: 'sub-template', key: name }
}

/** A name's type as an underscore path over a variable schema alone. */
export const pathTypeOf = (variables: z.ZodType, name: string): VariableType =>
    pathType(variables, name.split('_'), false)

export function unwrapped(schema: z.ZodType): z.ZodType {
    return schema instanceof z.ZodOptional || schema instanceof z.ZodNullable
        ? unwrapped(schema.unwrap() as z.ZodType)
        : schema
}

const KIND_OF_SCHEMA = new Map<z.ZodType, ValueKind>(
    Object.entries(VALUE_SCHEMAS).map(([kind, schema]) => [schema, kind as ValueKind]),
)

/** The kind a fact-schema leaf holds: a value schema from `@/lib/facts/values` by identity,
 *  or any plain string or closed vocabulary as text. A refined copy of a value schema is a
 *  different schema. */
export function valueKindOf(schema: z.ZodType): ValueKind | undefined {
    const bare = unwrapped(schema)
    return (
        KIND_OF_SCHEMA.get(bare) ??
        (bare instanceof z.ZodString || bare instanceof z.ZodEnum ? 'text' : undefined)
    )
}

/** The tokens a closed text fact holds, from its enum; a free text fact has none. */
function vocabularyOf(schema: z.ZodType): readonly string[] | undefined {
    const bare = unwrapped(schema)
    return bare instanceof z.ZodEnum ? (bare.options as string[]) : undefined
}

/** The schema of an object field one path segment names; an array is a leaf, and so is a
 *  value schema — a money value's amount and currency are its own, not path segments. */
export function childSchema(schema: z.ZodType, segment: string): z.ZodType | undefined {
    const bare = unwrapped(schema)
    return bare instanceof z.ZodObject &&
        !KIND_OF_SCHEMA.has(bare) &&
        Object.hasOwn(bare.shape, segment)
        ? (bare.shape[segment] as z.ZodType)
        : undefined
}

/** A variable's kind: a value kind, or a rendered message. */
export type VariableKind = ValueKind | 'message'

export type VariableType =
    | {
          readonly status: 'typed'
          readonly kind: VariableKind
          readonly optional: boolean
          /** A closed text fact's tokens: the only branches a `select` over it can take. */
          readonly vocabulary?: readonly string[]
      }
    | { readonly status: 'unresolved' }

/** What a name is typed against: a reader's variable schema and editorial constants, and the
 *  keys of the store files it reads — the sub-templates its copy may name. */
export interface VariableDeclarations<F> {
    /** Built from the value schemas in `@/lib/facts/values`, so each variable's kind is
     *  read from the schema its path walks to. */
    readonly variables: z.ZodType<F>
    readonly registry: VariableRegistry
    readonly keys: ReadonlySet<string>
}

const UNRESOLVED: VariableType = { status: 'unresolved' }
const typed = (kind: VariableKind, optional: boolean): VariableType => ({
    status: 'typed',
    kind,
    optional,
})

/** A name's type from the declarations alone. An editorial constant without a default may
 *  lack the entity, so it can be absent. A sub-template is typed present: copy naming it
 *  unguarded requires it, and the content gate checks its key is carried wherever that
 *  copy is read, so only a guarded reference may find it absent. */
export function variableType<F>(name: string, declarations: VariableDeclarations<F>): VariableType {
    const form = nameForm(name, declarations)
    switch (form.form) {
        case 'editorial':
            return form.constant
                ? typed(form.constant.kind, form.constant.default === undefined)
                : UNRESOLVED
        case 'path':
            return pathType(declarations.variables, form.segments, false)
        case 'sub-template':
            return declarations.keys.has(form.key) ? typed('message', false) : UNRESOLVED
    }
}

function pathType(schema: z.ZodType, segments: readonly string[], optional: boolean): VariableType {
    const isOptional = optional || unwrapped(schema) !== schema
    const [segment, ...rest] = segments
    if (segment === undefined) {
        const kind = valueKindOf(schema)
        const vocabulary = vocabularyOf(schema)
        return kind
            ? { ...typed(kind, isOptional), ...(vocabulary ? { vocabulary } : {}) }
            : UNRESOLVED
    }
    const child = childSchema(schema, segment)
    return child ? pathType(child, rest, isOptional) : UNRESOLVED
}
